/**
 * Slot Modal Component
 * Unified modal for adding and editing slots
 */
import { LitElement, html } from "lit";
import { store } from "../../../platform/store/DataStore.js";
import { SlotFormService } from "../services/slot-form.service.js";
import { SlotService } from "../services/slot.service.js";

export class SlotModal extends LitElement {
  static properties = {
    open: { type: Boolean },
    mode: { type: String }, // 'add' or 'edit'
    slotId: { type: Number },
    selectedInsertAfter: { type: String },
    startOptions: { type: Array },
    formValid: { type: Boolean },
  };

  constructor() {
    super();
    this.open = false;
    this.mode = "add";
    this.slotId = null;
    this.selectedInsertAfter = null;
    this.startOptions = [];
    this.formValid = false;
  }

  updated(changedProperties) {
    if (changedProperties.has("open") && this.open) {
      this._initializeForm();
    }
  }

  async _initializeForm() {
    await this.updateComplete;

    // Reset state
    const initialState =
      this.mode === "add"
        ? SlotFormService.getInitialStateForAdd()
        : SlotFormService.getInitialStateForEdit(this.slotId);

    this.selectedInsertAfter = initialState.selectedInsertAfter;
    this.startOptions = initialState.startOptions;
    this.formValid = initialState.formValid;

    // Get data if editing
    let slot = null;
    if (this.mode === "edit" && this.slotId) {
      slot = store.getSlot(this.slotId);
    }

    // Populate form
    SlotFormService.populateForm(this.renderRoot, slot, this.mode);

    this._updateFormValidity();
  }

  _onInsertAfterChange(e) {
    const insertVal = e.detail.value;
    this.selectedInsertAfter = insertVal;

    if (insertVal) {
      const dates = SlotService.getAvailableStartDates(insertVal);
      this.startOptions = dates.map((d) => ({
        value: d,
        label: this._formatDateYYMMDD(d),
      }));
    } else {
      this.startOptions = [];
    }

    this._updateFormValidity();
  }

  _handleInputChange() {
    this._updateFormValidity();
  }

  _updateFormValidity() {
    this.formValid = SlotFormService.isFormValid(
      this.renderRoot,
      this.mode,
      this.slotId
    );
  }

  async _handleSubmit(e) {
    e.preventDefault();
    if (!SlotFormService.isFormValid(this.renderRoot, this.mode, this.slotId)) {
      return;
    }

    const formData = SlotFormService.extractFormData(
      this.renderRoot,
      this.mode
    );
    const action = this.mode === "add" ? "add" : "update";
    this.dispatchEvent(
      new CustomEvent("modal-save", {
        detail: {
          action,
          slotId: this.slotId,
          formData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  _handleClose() {
    this.open = false;
    this.dispatchEvent(
      new CustomEvent("modal-close", {
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    if (!this.open) return html``;

    const title =
      this.mode === "add" ? "Lägg till kursperiod" : "Redigera kursperiod";
    const submitLabel = this.mode === "add" ? "Spara" : "Spara ändringar";

    return html`
      <henry-modal open title="${title}" @close="${this._handleClose}">
        <form
          @submit="${this._handleSubmit}"
          @input="${this._handleInputChange}"
          @change="${this._handleInputChange}"
          @select-change="${this._handleInputChange}"
        >
          <div
            style="display: flex; flex-direction: column; gap: var(--space-4);"
          >
            ${this.mode === "add"
              ? html`
                  <henry-select
                    id="insertAfter"
                    label="Infoga efter kursperiod"
                    size="6"
                    placeholder=""
                    ?hidePlaceholder=${true}
                    .options=${SlotService.getInsertAfterOptions()}
                    @select-change="${this._onInsertAfterChange}"
                    required
                  ></henry-select>

                  <henry-select
                    id="slotStart"
                    label="Startdatum"
                    size="6"
                    required
                    .options=${this.startOptions}
                    placeholder=""
                    ?hidePlaceholder=${true}
                  ></henry-select>
                `
              : html`
                  <henry-input
                    id="slotStart"
                    label="Startdatum"
                    type="date"
                    required
                  ></henry-input>
                `}
          </div>
        </form>

        <div slot="footer">
          <henry-button variant="secondary" @click="${this._handleClose}">
            Avbryt
          </henry-button>
          <henry-button
            variant="primary"
            ?disabled=${!this.formValid ||
            (this.mode === "add" &&
              this.selectedInsertAfter &&
              this.startOptions.length === 0)}
            @click="${() =>
              this.renderRoot.querySelector("form").requestSubmit()}"
          >
            ${submitLabel}
          </henry-button>
        </div>
      </henry-modal>
    `;
  }

  _formatDateYYMMDD(value) {
    if (!value) return "";
    const [datePart] = String(value).split("T");
    const parts = datePart.split("-");
    if (parts.length !== 3) return datePart;
    const [yyyy, mm, dd] = parts;
    if (!yyyy || !mm || !dd) return datePart;
    return `${yyyy.slice(-2)}${mm}${dd}`;
  }

  createRenderRoot() {
    return this;
  }
}

customElements.define("slot-modal", SlotModal);
